'use client';

import { FONT_OPTIONS } from '@/@account/types';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

const FONT_COOKIE = 'studio-font-preference';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

function loadGoogleFont(googleFontName: string) {
    const linkId = `google-font-${googleFontName}`;
    if (document.getElementById(linkId)) return;
    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${googleFontName}:wght@400;700&display=swap`;
    document.head.appendChild(link);
}

function applyFont(fontKey: string | null) {
    if (!fontKey || fontKey === 'inter') {
        document.documentElement.removeAttribute('data-font');
        return;
    }
    document.documentElement.setAttribute('data-font', fontKey);
    const fontOption = FONT_OPTIONS.find((f) => f.key === fontKey);
    if (fontOption?.googleFont) {
        loadGoogleFont(fontOption.googleFont);
    }
}

interface FontContextValue {
    selectedFont: string;
    setFont: (fontKey: string) => void;
}

const FontContext = createContext<FontContextValue>({
    selectedFont: 'inter',
    setFont: () => {},
});

export function useFontPreference() {
    return useContext(FontContext);
}

export function FontProvider({ children }: { children: ReactNode }) {
    const [selectedFont, setSelectedFont] = useState('inter');

    useEffect(() => {
        const saved = getCookie(FONT_COOKIE) as string | undefined;
        if (saved && saved !== 'inter') {
            setSelectedFont(saved);
            applyFont(saved);
        }
    }, []);

    const setFont = useCallback((fontKey: string) => {
        setSelectedFont(fontKey);
        if (fontKey === 'inter') {
            deleteCookie(FONT_COOKIE, { path: '/' });
        } else {
            setCookie(FONT_COOKIE, fontKey, { maxAge: COOKIE_MAX_AGE, path: '/' });
        }
        applyFont(fontKey);
    }, []);

    return (
        <FontContext.Provider value={{ selectedFont, setFont }}>{children}</FontContext.Provider>
    );
}
